import { Link } from "react-router-dom";
import { PublicHeader, PublicFooter } from "@/components/PublicHeader";
import { Mail } from "lucide-react";

const COMPANY = "Liza Tech Solution";
const BRAND = "NIGAMAI";
const EMAIL = "ai@nigamai.com";
const SITE = "nigamai.com";
const UPDATED = "June 2026";

const LegalLayout = ({ title, children, testid }) => (
  <div className="min-h-screen bg-[#0A0A0A] text-white flex flex-col" data-testid={testid}>
    <PublicHeader />
    <main className="flex-1 max-w-3xl mx-auto w-full px-6 py-14">
      <Link to="/" className="text-sm text-[#00FF66] hover:underline">← Back to home</Link>
      <h1 className="font-heading font-extrabold text-4xl sm:text-5xl tracking-tight mt-4 mb-2">{title}</h1>
      <p className="text-sm text-zinc-500 mb-10">Last updated: {UPDATED}</p>
      <div className="space-y-6 text-zinc-300 leading-relaxed text-[15px] legal-body">{children}</div>
    </main>
    <PublicFooter />
  </div>
);

const H = ({ children }) => <h2 className="text-white font-semibold text-lg mt-8 mb-2">{children}</h2>;
const P = ({ children }) => <p className="text-zinc-400">{children}</p>;
const UL = ({ items }) => (
  <ul className="list-disc pl-5 space-y-1.5 text-zinc-400">
    {items.map((it, i) => <li key={i}>{it}</li>)}
  </ul>
);

export function Terms() {
  return (
    <LegalLayout title="Terms & Conditions" testid="page-terms">
      <P>Welcome to {BRAND}. These Terms & Conditions ("Terms") govern your access to and use of the {BRAND} website, applications and services (collectively, the "Service"), operated by {COMPANY} ("we", "us", "our"). By creating an account or using the Service, you agree to these Terms. If you do not agree, please do not use the Service.</P>

      <H>1. The Service</H>
      <P>{BRAND} is an AI-powered assistant that generates text, code, images, audio, documents and video, and related features. The Service is provided on a credit-based model. Certain AI models and file types consume different amounts of credits, shown in the app.</P>

      <H>2. Accounts</H>
      <UL items={[
        "You must provide accurate information when registering and keep your password secure.",
        "You are responsible for all activity that occurs under your account.",
        "You must be at least 18 years old, or use the Service under the supervision of a parent or guardian.",
      ]} />

      <H>3. Credits & Payments</H>
      <UL items={[
        "Credits are purchased in packs at the prices displayed in Indian Rupees (INR) at checkout.",
        "Payments are processed securely by our payment partner (Cashfree). We do not store your card details.",
        "Credits are added to your wallet on successful payment. Free and bonus credits may be granted at our discretion.",
        "Prices and credit costs may change; changes apply to future purchases only.",
      ]} />

      <H>4. Acceptable Use</H>
      <P>You agree not to use the Service to generate or distribute unlawful, harmful, infringing, hateful, or abusive content, to violate others' rights, to attempt to reverse engineer or disrupt the Service, or to resell the Service without our written permission.</P>

      <H>5. Intellectual Property</H>
      <P>Subject to these Terms and applicable law, content you generate with the Service is yours to use. The {BRAND} brand, software and underlying technology remain the property of {COMPANY}. All copyrights are reserved by {COMPANY}.</P>

      <H>6. AI Output Disclaimer</H>
      <P>AI-generated output may be inaccurate or incomplete. You are responsible for reviewing and verifying any output before relying on it. The Service is provided "as is" without warranties of any kind.</P>

      <H>7. Limitation of Liability</H>
      <P>To the maximum extent permitted by law, {COMPANY} shall not be liable for any indirect, incidental or consequential damages. Our total liability shall not exceed the amount you paid us in the 3 months preceding the claim.</P>

      <H>8. Termination</H>
      <P>We may suspend or terminate accounts that violate these Terms. You may stop using the Service at any time.</P>

      <H>9. Governing Law</H>
      <P>These Terms are governed by the laws of India, and disputes are subject to the courts of India.</P>

      <H>10. Contact</H>
      <P>Questions about these Terms? Email us at <a className="text-[#00FF66] hover:underline" href={`mailto:${EMAIL}`}>{EMAIL}</a>.</P>
    </LegalLayout>
  );
}

export function Privacy() {
  return (
    <LegalLayout title="Privacy Policy" testid="page-privacy">
      <P>{COMPANY} ("we") respects your privacy. This Privacy Policy explains what information we collect through {BRAND} and how we use and protect it.</P>

      <H>1. Information We Collect</H>
      <UL items={[
        "Account information: your name and email address.",
        "Usage data: your conversations, prompts, generated files, credit usage and activity within the Service.",
        "Payment data: processed by our payment partner (Cashfree). We receive transaction status and amounts but do NOT store your card or bank details.",
        "Technical data: log data such as device/browser information used to operate and secure the Service.",
      ]} />

      <H>2. How We Use Information</H>
      <UL items={[
        "To provide, maintain and improve the Service.",
        "To process payments and manage your credit balance.",
        "To send service and account emails, and (where permitted) product updates.",
        "To detect, prevent and address fraud, abuse and security issues.",
      ]} />

      <H>3. Sharing</H>
      <P>We share data only with service providers who help us operate the Service (for example AI model providers, payment processor, and email delivery) under appropriate confidentiality obligations, or when required by law. We do not sell your personal data.</P>

      <H>4. Data Security & Retention</H>
      <P>Passwords are stored using strong one-way hashing and are never accessible in plain text. We retain your data for as long as your account is active or as needed to provide the Service and meet legal obligations.</P>

      <H>5. Your Rights</H>
      <P>You may request access to, correction of, or deletion of your personal data by emailing <a className="text-[#00FF66] hover:underline" href={`mailto:${EMAIL}`}>{EMAIL}</a>.</P>

      <H>6. Cookies</H>
      <P>We use essential cookies/local storage to keep you signed in and to operate core features.</P>

      <H>7. Contact</H>
      <P>For any privacy questions, contact <a className="text-[#00FF66] hover:underline" href={`mailto:${EMAIL}`}>{EMAIL}</a>.</P>
    </LegalLayout>
  );
}

export function Refund() {
  return (
    <LegalLayout title="Refund & Cancellation Policy" testid="page-refund">
      <P>This Refund & Cancellation Policy applies to purchases of credit packs on {BRAND}, operated by {COMPANY}.</P>

      <H>1. Nature of the Product</H>
      <P>{BRAND} sells digital credits that are consumed instantly to generate AI content. Because credits are a digital, on-demand product, purchases are generally non-refundable once credits have been added to your wallet or used.</P>

      <H>2. Eligible Refunds</H>
      <UL items={[
        "Duplicate charge: if you were charged more than once for the same order, the extra amount will be refunded.",
        "Failed delivery: if your payment succeeded but credits were not added to your wallet and we cannot resolve it, you will be refunded.",
        "Unauthorized/erroneous transaction reported within 7 days and verified by us.",
      ]} />

      <H>3. Non-Refundable</H>
      <UL items={[
        "Credits that have already been consumed.",
        "Change of mind after credits are successfully delivered.",
        "Dissatisfaction with AI output quality (AI results can vary).",
      ]} />

      <H>4. How to Request a Refund</H>
      <P>Email <a className="text-[#00FF66] hover:underline" href={`mailto:${EMAIL}`}>{EMAIL}</a> with your registered email and the order/transaction ID within 7 days of the transaction. We will review and respond within 3–5 business days.</P>

      <H>5. Refund Timeline</H>
      <P>Approved refunds are processed back to the original payment method via our payment partner (Cashfree) and typically reflect within 5–7 business days, depending on your bank.</P>

      <H>6. Cancellation</H>
      <P>You may stop purchasing at any time. There are no recurring subscriptions unless explicitly stated; credit packs are one-time purchases.</P>

      <H>7. Contact</H>
      <P>For refund or cancellation help, contact <a className="text-[#00FF66] hover:underline" href={`mailto:${EMAIL}`}>{EMAIL}</a>.</P>
    </LegalLayout>
  );
}

export function Shipping() {
  return (
    <LegalLayout title="Shipping & Delivery Policy" testid="page-shipping">
      <P>{BRAND} is a fully digital service operated by {COMPANY}. No physical goods are shipped.</P>

      <H>1. Digital Delivery</H>
      <P>Upon successful payment, credits are delivered electronically and added to your {BRAND} account wallet instantly (typically within a few seconds). AI-generated files are delivered directly within the app for you to view and download.</P>

      <H>2. Delivery Confirmation</H>
      <P>You can verify delivery by checking your updated credit balance in the app and your usage history. A confirmation is also available on the billing page after checkout.</P>

      <H>3. Delivery Issues</H>
      <P>If your payment succeeded but credits were not delivered within 30 minutes, please email <a className="text-[#00FF66] hover:underline" href={`mailto:${EMAIL}`}>{EMAIL}</a> with your transaction ID and we will resolve it promptly.</P>

      <H>4. Contact</H>
      <P>For any delivery questions, contact <a className="text-[#00FF66] hover:underline" href={`mailto:${EMAIL}`}>{EMAIL}</a>.</P>
    </LegalLayout>
  );
}

export function Contact() {
  return (
    <LegalLayout title="Contact Us" testid="page-contact">
      <P>We'd love to hear from you. For support, billing, refunds, privacy requests or any other questions about {BRAND}, please reach out.</P>

      <div className="rounded-2xl border border-white/10 bg-[#141415] p-6 mt-4">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-[#00FF66]/15 flex items-center justify-center">
            <Mail className="w-5 h-5 text-[#00FF66]" />
          </div>
          <div>
            <p className="text-sm text-zinc-500">Email us</p>
            <a className="text-lg font-semibold text-white hover:text-[#00FF66] transition-colors" href={`mailto:${EMAIL}`} data-testid="contact-email">{EMAIL}</a>
          </div>
        </div>
      </div>

      <H>Business Details</H>
      <UL items={[
        `Service: ${BRAND} (an AI assistant platform)`,
        `Operated by: ${COMPANY}`,
        "Founder & CEO: Vishal R Nigam",
        `Website: ${SITE}`,
        `Support email: ${EMAIL}`,
      ]} />

      <H>Support Hours</H>
      <P>We aim to respond to all emails within 1–2 business days.</P>
    </LegalLayout>
  );
}
